/**
 * Public App Component
 * 
 * Handles routing for public website (www.eduna.com)
 * No authentication required
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBoundary from '../components/common/ErrorBoundary';
import Header from '../components/public/Header';
import Footer from '../components/public/Footer';

// Lazy load public components
const Homepage = React.lazy(() => import('../views/public/Homepage'));
const Features = React.lazy(() => import('../views/public/Features'));
const Pricing = React.lazy(() => import('../views/public/Pricing'));
const About = React.lazy(() => import('../views/public/About'));
const FAQ = React.lazy(() => import('../views/public/FAQ'));
const Contact = React.lazy(() => import('../views/public/Contact'));
const Feedback = React.lazy(() => import('../views/public/Feedback'));
const PrivacyPolicy = React.lazy(() => import('../views/public/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('../views/public/TermsOfService'));
const CookiePolicy = React.lazy(() => import('../views/public/CookiePolicy'));
const GDPR = React.lazy(() => import('../views/public/GDPR'));
const TenantRegistration = React.lazy(() => import('../views/public/TenantRegistration'));
const EmailVerification = React.lazy(() => import('../views/public/EmailVerification'));
const DataRestoration = React.lazy(() => import('../views/public/DataRestoration'));
const Login = React.lazy(() => import('../views/auth/Login'));
const ForgotPassword = React.lazy(() => import('../views/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('../views/auth/ResetPassword'));
const GoogleAuthSuccess = React.lazy(() => import('../views/auth/GoogleAuthSuccess'));

function PublicApp() {
  return (
    <ErrorBoundary>
      <Helmet>
        <title>EDUNA - Educational Management Platform</title>
        <meta name="description" content="Complete educational management system for academies and schools" />
      </Helmet>
      
      <div className="d-flex flex-column min-vh-100">
        <Header />
        <main className="flex-grow-1">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public pages */}
              <Route path="/" element={<Homepage />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/feedback" element={<Feedback />} />
              
              {/* Legal pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/gdpr" element={<GDPR />} />
              
              {/* Registration */}
              <Route path="/register" element={<TenantRegistration />} />
              <Route path="/register-academy" element={<TenantRegistration />} />
              <Route path="/verify-email/:token" element={<EmailVerification />} />
              
              {/* Data Restoration */}
              <Route path="/restore-data/:token" element={<DataRestoration />} />
              
              {/* Auth pages */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/google/success" element={<GoogleAuthSuccess />} />
              
              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </ErrorBoundary>
  );
}

export default PublicApp;
